import { NetworkStatus } from '@apollo/client';
import {
  AllForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  CacheKeys,
  ErrorRetry,
  MockAllForeman,
  MockAllGeneralForeman,
  MockAllSuperintendent,
  MockUnassignedForeman,
  MockUnassignedGeneralForeman,
  MockUnassignedSuperintendent,
  PlanningActionType,
  UnassignedForemanId,
  UnassignedGeneralForemanId,
  UnassignedSuperintendentId,
  useAppUser,
  useCrewChangeSubscriptionContext,
  useCrewHierachy,
  useDebounce,
  useName,
  usePlanning,
  useSessionStorage,
} from '@bechtel/client-common';
import {
  AvatarSize,
  Box,
  Combobox,
  FormControl,
  Select,
  SelectOptionStyled,
  SelectStyled,
  TextFieldAppearance,
  TextFieldSize,
  theme,
} from '@bechtel/gds';
import { CrewMember, CrewMemberList, PersonaType } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { find, forEach, get } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { usePlanningPermissions } from '~/hooks';
import { AvatarRegularStyled, HeadingStyled, NoHierachyTextStyled } from './styled';
//TODO:enable hours code  disable for temporarily time to improve planning module performance
// import { useEarnableHours } from './hooks';

const ItemComponent = ({ item }: any) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();

  //const { earnableHours, loading } = useEarnableHours(item);
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="600" css={{ paddingBottom: `${theme.spacings[300]}` }}>
            {item?.id === AllForemanId ||
            item?.id === AllGeneralForemanId ||
            item?.id === AllSuperintendentId ||
            item?.id === UnassignedSuperintendentId ||
            item?.id === UnassignedGeneralForemanId ||
            item?.id === UnassignedForemanId
              ? i18n._(item.firstName)
              : getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
        </Box>
        {/* <Text size="200" color="grey.600" style={{ whiteSpace: 'nowrap' }}>
          {loading && <Spinner size={SpinnerSize.SMALL} />}
          {!loading && `${earnableHours ? earnableHours : 0} ${t`hrs`}`}
        </Text> */}
      </Box>
    </Box>
  );
};
// TODO - Selected box height need to be 58px to match the design but there is no optin in GDS to do so.
const SelectedItemComponent = ({ item }: any) => {
  const { getFullName } = useName();
  // const { earnableHours, loading } = useEarnableHours(item);
  const { i18n } = useLingui();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        <AvatarRegularStyled
          hasImageUrl={item && item.imageUrl ? true : false}
          size={AvatarSize.MEDIUM}
          src={item && item.imageUrl ? item.imageUrl : undefined}
        />
      </Box>
      <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
        <Box flexDirection={{ sm: 'column' }}>
          <HeadingStyled weight="500">
            {item?.id == AllForemanId ||
            item?.id == AllGeneralForemanId ||
            item?.id == AllSuperintendentId ||
            item?.id === UnassignedSuperintendentId ||
            item?.id === UnassignedGeneralForemanId ||
            item?.id === UnassignedForemanId
              ? i18n._(item.firstName)
              : getFullName(item.firstName, item.lastName)}
          </HeadingStyled>
          {/* <Text size="200" color="grey.1000">
             {loading && <Spinner size={SpinnerSize.SMALL} />}
            {!loading && `${earnableHours ? earnableHours : 0} ${t`hrs`}`}
          </Text> */}
        </Box>
      </Box>
    </Box>
  );
};

interface ForemanExpandedProps {
  hidden: boolean;
}
export const ForemanExpanded: FC<ForemanExpandedProps> = ({ hidden }) => {
  const { canPlanAllMilestones } = usePlanningPermissions();
  const { getFullName } = useName();
  const { user } = useAppUser();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText?.trim(), 500);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const { planningDispatch, selectedForeman } = usePlanning();
  const userCrewMember = user?.crewMember;
  const { affectedCrewMembers, setAffectedCrewMembers } = useCrewChangeSubscriptionContext();
  const [planningSelectedSuperintendent, setPlanningSelectedSuperintendent] = useSessionStorage<CrewMember>(
    CacheKeys.PlanningSelectedSuperintendent,
    userCrewMember?.persona === PersonaType.SUPERINTENDENT ? userCrewMember : MockAllSuperintendent
  );
  const [planningSelectedGeneralForeman, setPlanningSelectedGeneralForeman] = useSessionStorage<CrewMember>(
    CacheKeys.PlanningSelectedGeneralForeman,
    MockAllGeneralForeman
  );
  const [planningSelectedForeman, setPlanningSelectedForeman] = useSessionStorage<CrewMember>(
    CacheKeys.PlanningSelectedForeman,
    MockAllForeman
  );

  const [
    getSuperintendentList,
    { data: supData, loading: supLoading, error: supError, refetch: supRefetch, networkStatus: supNetworkStatus },
  ] = useCrewHierachy(true);

  const [
    getGeneralForemanList,
    {
      data: generalForemanData,
      error: generalForemanError,
      loading: generalForemanLoading,
      refetch: generalForemanRefetch,
      networkStatus: generalForemanNetworkStatus,
    },
  ] = useCrewHierachy(true);

  const [
    getForemanList,
    {
      data: foremanData,
      error: foremanError,
      loading: foremanLoading,
      refetch: foremanRefetch,
      networkStatus: foremanNetworkStatus,
    },
  ] = useCrewHierachy(true);

  // Track initial mount to refetch foreman list on component mount
  // This ensures planningAiRequest data is fresh when returning from another page
  const isInitialMount = useRef(true);
  const hasCompletedInitialSync = useRef(false);

  let superintendentList = get(supData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  let generalForemanList = get(generalForemanData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  let foremanList = get(foremanData, 'crewHierarchy.items', []) as CrewMemberList['items'];
  if (superintendentList && superintendentList.length > 0) {
    if (!searchText.length)
      superintendentList = [MockAllSuperintendent, ...superintendentList, MockUnassignedSuperintendent];
    else superintendentList = [...superintendentList];
  }
  if (generalForemanList && generalForemanList.length > 0) {
    generalForemanList = [MockAllGeneralForeman, ...generalForemanList, MockUnassignedGeneralForeman];
  }
  if (foremanList && foremanList.length > 0) {
    foremanList = [MockAllForeman, ...foremanList, MockUnassignedForeman];
  }
  const foremanDisabled =
    foremanList.length === 0 ||
    (planningSelectedGeneralForeman &&
      (planningSelectedGeneralForeman.id === UnassignedGeneralForemanId ||
        planningSelectedGeneralForeman.id === AllGeneralForemanId));

  //Logged in user is general foreman, set superintendent, set logged in user as general foreman, load foreman list
  useEffect(() => {
    if (userCrewMember?.persona === PersonaType.GENERAL_FOREMAN) {
      setPlanningSelectedSuperintendent(userCrewMember.supervisor);
      setPlanningSelectedGeneralForeman(userCrewMember);
      // When superidendent or gf for foreman is coming null
      if (!userCrewMember?.supervisor || !userCrewMember?.supervisor?.supervisor)
        getForemanList({
          variables: {
            input: {
              superintendentId: planningSelectedSuperintendent?.id,
              generalForemanId: planningSelectedGeneralForeman?.id,
            },
          },
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCrewMember]);

  //Logged in user is foreman, set superintendent, set general foreman, set logged in user as foreman
  useEffect(() => {
    if (userCrewMember?.persona === PersonaType.FOREMAN) {
      setPlanningSelectedSuperintendent(userCrewMember.supervisor?.supervisor);
      setPlanningSelectedGeneralForeman(userCrewMember.supervisor);
      setPlanningSelectedForeman(userCrewMember);
      // Fetch foreman list via crewHierarchy to get planningAiRequest data
      // userCrewMember from auth context does not include planningAiRequest
      if (userCrewMember.supervisor?.supervisor?.id && userCrewMember.supervisor?.id) {
        getForemanList({
          variables: {
            input: {
              superintendentId: userCrewMember.supervisor.supervisor.id,
              generalForemanId: userCrewMember.supervisor.id,
            },
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCrewMember]);

  useEffect(() => {
    if (
      !canPlanAllMilestones &&
      (userCrewMember?.persona === PersonaType.GENERAL_FOREMAN || userCrewMember?.persona === PersonaType.FOREMAN)
    )
      return;
    getSuperintendentList({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchText, canPlanAllMilestones]);

  useEffect(() => {
    if (
      !(
        planningSelectedSuperintendent &&
        (planningSelectedSuperintendent.id === AllSuperintendentId ||
          planningSelectedSuperintendent.id === UnassignedSuperintendentId ||
          !planningSelectedSuperintendent)
      ) &&
      (canPlanAllMilestones ||
        !(userCrewMember?.persona === PersonaType.GENERAL_FOREMAN || userCrewMember?.persona === PersonaType.FOREMAN))
    ) {
      getGeneralForemanList({
        variables: {
          input: {
            superintendentId: planningSelectedSuperintendent?.id,
          },
        },
      });
    }
    planningDispatch({
      type: PlanningActionType.CrewMemberSelected,
      payload: {
        isReplan: false,
        selectedSuperintendent: planningSelectedSuperintendent,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningSelectedSuperintendent, planningDispatch]);

  useEffect(() => {
    if (
      !(
        planningSelectedGeneralForeman &&
        (planningSelectedGeneralForeman.id === UnassignedGeneralForemanId ||
          planningSelectedGeneralForeman.id === AllGeneralForemanId ||
          !planningSelectedSuperintendent)
      ) &&
      (canPlanAllMilestones || userCrewMember?.persona !== PersonaType.FOREMAN)
    ) {
      getForemanList({
        variables: {
          input: {
            superintendentId: planningSelectedSuperintendent?.id,
            generalForemanId: planningSelectedGeneralForeman?.id,
          },
        },
      });
    }
    planningDispatch({
      type: PlanningActionType.CrewMemberSelected,
      payload: {
        isReplan: false,
        selectedGeneralForeman: planningSelectedGeneralForeman,
      },
    });
  }, [
    planningSelectedGeneralForeman,
    planningDispatch,
    planningSelectedSuperintendent,
    userCrewMember?.persona,
    getForemanList,
  ]);

  useEffect(() => {
    // Don't dispatch stale session storage data on initial mount
    // Wait for fresh data to be fetched first
    if (isInitialMount.current) {
      return;
    }

    if (!planningSelectedForeman) {
      return;
    }

    planningDispatch({
      type: PlanningActionType.CrewMemberSelected,
      payload: {
        isReplan: false,
        selectedForeman: planningSelectedForeman,
      },
    });
  }, [planningSelectedForeman, planningDispatch]);

  // Refetch foreman list when AI request status changes (subscription updates context)
  // so the dropdown items contain the latest planningAiRequest — prevents stale object
  // being re-selected when user switches foreman away and back
  useEffect(() => {
    if (
      !planningSelectedSuperintendent?.id ||
      !planningSelectedGeneralForeman?.id ||
      planningSelectedGeneralForeman.id === UnassignedGeneralForemanId ||
      planningSelectedGeneralForeman.id === AllGeneralForemanId
    )
      return;
    foremanRefetch({
      input: {
        superintendentId: planningSelectedSuperintendent.id,
        generalForemanId: planningSelectedGeneralForeman.id,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForeman?.planningAiRequest?.id, selectedForeman?.planningAiRequest?.status]);

  // Sync selected foreman in session storage when the foreman list refreshes with fresh data
  useEffect(() => {
    if (!planningSelectedForeman?.id || !foremanList.length) return;
    const freshForeman = foremanList.find((f) => f.id === planningSelectedForeman.id);
    if (!freshForeman) return;

    // On first sync after mount, ALWAYS update to ensure fresh data reaches context
    // After that, only update if AI request actually changed to prevent infinite loops
    const isFirstSync = !hasCompletedInitialSync.current;
    const hasAiRequestChanged =
      planningSelectedForeman.planningAiRequest?.id !== freshForeman.planningAiRequest?.id ||
      planningSelectedForeman.planningAiRequest?.status !== freshForeman.planningAiRequest?.status ||
      planningSelectedForeman.planningAiRequest?.count !== freshForeman.planningAiRequest?.count;

    if (isFirstSync || hasAiRequestChanged) {
      // Mark initial mount complete BEFORE updating, so dispatch won't be skipped
      if (isFirstSync) {
        isInitialMount.current = false;
      }

      // Update to ensure we have the latest planningAiRequest data
      setPlanningSelectedForeman(freshForeman);
      hasCompletedInitialSync.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foremanList]);

  // Refetch foreman list on initial mount to ensure planningAiRequest data is fresh
  // This fixes the issue where navigating away and back causes stale AI status from session storage
  useEffect(() => {
    if (!isInitialMount.current) return;

    // Only refetch if there's a selected foreman that might have stale AI request data
    if (
      planningSelectedForeman?.id &&
      planningSelectedForeman.id !== AllForemanId &&
      planningSelectedForeman.id !== UnassignedForemanId &&
      planningSelectedSuperintendent?.id &&
      planningSelectedGeneralForeman?.id &&
      planningSelectedGeneralForeman.id !== UnassignedGeneralForemanId &&
      planningSelectedGeneralForeman.id !== AllGeneralForemanId
    ) {
      // Force network fetch by refetching with same variables
      getForemanList({
        variables: {
          input: {
            superintendentId: planningSelectedSuperintendent.id,
            generalForemanId: planningSelectedGeneralForeman.id,
          },
        },
        fetchPolicy: 'network-only', // Force network fetch, not cache
      });
    } else {
      // If there's no foreman to refetch, mark initial mount as complete
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (affectedCrewMembers.length === 0) return;
    forEach(affectedCrewMembers, (affectedCrewMember) => {
      const updatedCrewMember = find(
        [planningSelectedForeman, planningSelectedGeneralForeman, planningSelectedSuperintendent],
        (item: CrewMember) => item.id === affectedCrewMember.crewMemberId
      );
      if (updatedCrewMember) {
        if (updatedCrewMember.persona === PersonaType.SUPERINTENDENT) {
          setPlanningSelectedForeman(MockAllGeneralForeman);
        } else if (updatedCrewMember.persona === PersonaType.GENERAL_FOREMAN) {
          setPlanningSelectedForeman(MockAllForeman);
        }
      }
    });
    setAffectedCrewMembers([]);
  }, [affectedCrewMembers]);

  return (
    <Box px={{ sm: '500' }} pt={{ sm: '500' }} flexDirection={{ sm: 'column' }} hidden={hidden}>
      {!supLoading && supError && (
        <ErrorRetry refetch={supRefetch}>
          <Trans>There was a problem loading Superintendents</Trans>
        </ErrorRetry>
      )}
      {!supError && (
        <FormControl
          label={t`Superintendent`}
          css={{ marginBottom: theme.spacings[550] }}
          data-testid="type-superintendent"
        >
          <Combobox
            ariaLabel="superintendent"
            size={TextFieldSize.LARGE}
            items={superintendentList}
            appearance={TextFieldAppearance.NORMAL}
            disabled={
              !canPlanAllMilestones &&
              (userCrewMember?.persona === PersonaType.GENERAL_FOREMAN ||
                userCrewMember?.persona === PersonaType.FOREMAN)
            }
            itemString={itemToString}
            itemComponent={ItemComponent}
            selectedComponent={SelectedItemComponent}
            selectedItem={planningSelectedSuperintendent}
            onSelect={async (selectedItem: any) => {
              setPlanningSelectedSuperintendent(selectedItem);
              setPlanningSelectedGeneralForeman(MockAllGeneralForeman);
              setPlanningSelectedForeman(MockAllForeman);
            }}
            css={`
              ${SelectStyled} {
                height: ${({ theme }) => theme.spacings[900]};
              }
              ${SelectOptionStyled} {
                padding: ${({ theme }) => theme.spacings[200]} ${({ theme }) => theme.spacings[500]};
              }
            `}
            term={searchText}
            setTerm={setSearchText}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchText(event.target.value);
            }}
            restorePreviousOnSkip
            loading={supLoading || supNetworkStatus === NetworkStatus.refetch}
            messageOnNoResults={t`No users found`}
          ></Combobox>
        </FormControl>
      )}
      {!generalForemanLoading && generalForemanError && (
        <ErrorRetry refetch={generalForemanRefetch}>
          <Trans>There was a problem loading General Foreman</Trans>
        </ErrorRetry>
      )}
      {!generalForemanError && (
        <FormControl
          label={t`General Foreman`}
          css={{ marginBottom: theme.spacings[550] }}
          data-testid="type-general-foreman"
        >
          <Select
            size={TextFieldSize.LARGE}
            appearance={TextFieldAppearance.NORMAL}
            items={generalForemanList}
            itemComponent={ItemComponent}
            disabled={
              generalForemanList.length === 0 ||
              planningSelectedSuperintendent?.id === AllSuperintendentId ||
              planningSelectedSuperintendent?.id === UnassignedSuperintendentId
            }
            selectedComponent={SelectedItemComponent}
            selectedItem={
              canPlanAllMilestones
                ? planningSelectedGeneralForeman
                : userCrewMember?.persona === PersonaType.GENERAL_FOREMAN
                  ? userCrewMember
                  : planningSelectedGeneralForeman
            }
            onSelectedItemChange={async (changes: any) => {
              setPlanningSelectedGeneralForeman(changes.selectedItem);
              setPlanningSelectedForeman(MockAllForeman);
            }}
            css={`
              ${SelectStyled} {
                height: ${({ theme }) => theme.spacings[900]};
              }
              ${SelectOptionStyled} {
                padding: ${({ theme }) => theme.spacings[200]} ${({ theme }) => theme.spacings[500]};
              }
            `}
            loading={generalForemanLoading || generalForemanNetworkStatus === NetworkStatus.refetch}
          ></Select>
        </FormControl>
      )}
      {!foremanLoading && foremanError && (
        <ErrorRetry refetch={foremanRefetch}>
          <Trans>There was a problem loading Foreman</Trans>
        </ErrorRetry>
      )}
      {!foremanError && (
        <FormControl label={t`Foreman`} css={{ marginBottom: theme.spacings[550] }} data-testid="type-Foreman">
          <Select
            size={TextFieldSize.LARGE}
            appearance={TextFieldAppearance.NORMAL}
            items={foremanList}
            itemComponent={ItemComponent}
            selectedItem={planningSelectedForeman}
            disabled={foremanDisabled}
            selectedComponent={SelectedItemComponent}
            onSelectedItemChange={async (changes: any) => {
              planningDispatch({
                type: PlanningActionType.ResetWorkPackageSelection,
                payload: { selectedWpList: [] },
              });
              setPlanningSelectedForeman(changes.selectedItem);
            }}
            css={`
              ${SelectStyled} {
                height: ${({ theme }) => theme.spacings[900]};
              }
              ${SelectOptionStyled} {
                padding: ${({ theme }) => theme.spacings[200]} ${({ theme }) => theme.spacings[500]};
              }
            `}
            loading={foremanLoading || foremanNetworkStatus === NetworkStatus.refetch}
          ></Select>
        </FormControl>
      )}
      {((userCrewMember.persona == PersonaType.GENERAL_FOREMAN && !userCrewMember?.supervisor) ||
        (userCrewMember.persona == PersonaType.FOREMAN && !userCrewMember?.supervisor) ||
        (userCrewMember.persona == PersonaType.FOREMAN && !userCrewMember?.supervisor?.supervisor)) && (
        <NoHierachyTextStyled family="graphik" color={'red.700'} size="400" weight="600">
          <Trans>No Hierachy Found</Trans>
        </NoHierachyTextStyled>
      )}
    </Box>
  );
};
